import React, { useState } from "react";
import { Box, IconButton, Popover, Typography } from "@mui/material";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useTranslation } from "react-i18next";
import color from "../../../Constants/colors";
import { useWorkspaceColors } from "../../../Contexts/WorkspaceColorContext";
import { addDays, dayKey, startOfWeek } from "../Calendar/voicesCalendarDates";

const PRESETS = [1, 7, 30, 90];

const MAX_DAYS = 366;

interface Props {
  preset: number | null;
  from: string;
  to: string;
  onPreset: (days: number) => void;
  onCustom: (from: string, to: string) => void;
}

const toDate = (key: string) => new Date(`${key}T00:00:00`);

const daysBetween = (first: string, second: string) =>
  Math.round(Math.abs(toDate(second).getTime() - toDate(first).getTime()) / 86400000);

const VoicesUsagePeriod: React.FC<Props> = ({ preset, from, to, onPreset, onCustom }) => {
  const { t, i18n } = useTranslation("voicesUsage");
  const { colors: workspace } = useWorkspaceColors();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [month, setMonth] = useState(() => toDate(to));
  const [pending, setPending] = useState<string | null>(null);
  const [hovered, setHovered] = useState<string | null>(null);

  const today = dayKey(new Date());
  const shortDate = (key: string) =>
    toDate(key).toLocaleDateString(i18n.language, { day: "numeric", month: "short" });

  const segmentSx = (selected: boolean) => ({
    display: "flex",
    alignItems: "center",
    gap: 0.75,
    px: 1.25,
    py: 0.5,
    border: 0,
    borderRadius: "8px",
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: 12,
    fontWeight: 600,
    whiteSpace: "nowrap",
    color: selected ? color.gray900 : color.gray600,
    backgroundColor: selected ? "white" : "transparent",
    boxShadow: selected ? "0 1px 2px rgba(16, 24, 40, 0.08)" : "none",
    "&:hover": { color: color.gray900 },
    "&:focus-visible": { outline: `2px solid ${workspace.primary_dark}`, outlineOffset: 1 },
  });

  const openPicker = (event: React.MouseEvent<HTMLElement>) => {
    setMonth(toDate(to));
    setPending(null);
    setHovered(null);
    setAnchor(event.currentTarget);
  };

  const pick = (key: string) => {
    if (!pending) {
      setPending(key);
      return;
    }
    onCustom(key < pending ? key : pending, key < pending ? pending : key);
    setPending(null);
    setAnchor(null);
  };

  const rangeEdges = () => {
    if (!pending) return preset === null ? [from, to] : [null, null];
    const other = hovered ?? pending;
    return other < pending ? [other, pending] : [pending, other];
  };
  const [rangeStart, rangeEnd] = rangeEdges();

  const monthStart = new Date(month.getFullYear(), month.getMonth(), 1);
  const gridStart = startOfWeek(monthStart);
  const cells = Array.from({ length: 42 }, (_, index) => addDays(gridStart, index));
  const weekdays = cells.slice(0, 7).map((day) =>
    day.toLocaleDateString(i18n.language, { weekday: "narrow" })
  );
  const isCurrentMonth =
    month.getFullYear() === new Date().getFullYear() && month.getMonth() === new Date().getMonth();

  return (
    <>
      <Box
        role="radiogroup"
        sx={{
          display: "inline-flex",
          flexWrap: "wrap",
          p: 0.25,
          borderRadius: "10px",
          backgroundColor: color.gray100,
          border: `1px solid ${color.gray200}`,
        }}
      >
        {PRESETS.map((days) => (
          <Box
            key={days}
            component="button"
            type="button"
            role="radio"
            aria-checked={preset === days}
            onClick={() => onPreset(days)}
            sx={segmentSx(preset === days)}
          >
            {t("presetDays", { count: days })}
          </Box>
        ))}
        <Box
          component="button"
          type="button"
          role="radio"
          aria-checked={preset === null}
          aria-haspopup="dialog"
          onClick={openPicker}
          sx={segmentSx(preset === null)}
        >
          <CalendarMonthOutlinedIcon sx={{ fontSize: 15 }} />
          {preset === null
            ? from === to
              ? shortDate(from)
              : `${shortDate(from)} – ${shortDate(to)}`
            : t("custom")}
        </Box>
      </Box>

      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: { mt: 0.75, p: 1.5, width: 280, borderRadius: "12px", backgroundColor: "white" },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
          <IconButton
            size="small"
            aria-label={t("previousMonth")}
            onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}
            sx={{ color: color.gray700 }}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: color.gray900 }}>
            {month.toLocaleDateString(i18n.language, { month: "long", year: "numeric" })}
          </Typography>
          <IconButton
            size="small"
            aria-label={t("nextMonth")}
            disabled={isCurrentMonth}
            onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}
            sx={{ color: color.gray700 }}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box
          sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", rowGap: 0.25 }}
          onMouseLeave={() => setHovered(null)}
        >
          {weekdays.map((weekday, index) => (
            <Typography
              key={index}
              sx={{ fontSize: 11, color: color.gray500, textAlign: "center", pb: 0.5 }}
            >
              {weekday}
            </Typography>
          ))}
          {cells.map((day) => {
            const key = dayKey(day);
            const outside = day.getMonth() !== month.getMonth();
            const disabled = key > today || (!!pending && daysBetween(pending, key) >= MAX_DAYS);
            const edge = key === rangeStart || key === rangeEnd;
            const inside = !!rangeStart && !!rangeEnd && key > rangeStart && key < rangeEnd;
            return (
              <Box
                key={key}
                component="button"
                type="button"
                disabled={disabled}
                onClick={() => pick(key)}
                onMouseEnter={() => setHovered(key)}
                sx={{
                  height: 32,
                  border: 0,
                  fontFamily: "inherit",
                  fontSize: 12,
                  cursor: disabled ? "default" : "pointer",
                  borderRadius: edge ? "8px" : inside ? 0 : "8px",
                  fontWeight: edge || key === today ? 700 : 400,
                  color: disabled
                    ? color.gray300
                    : edge
                      ? "white"
                      : outside
                        ? color.gray400
                        : color.gray900,
                  backgroundColor: edge
                    ? workspace.accent
                    : inside
                      ? color.orange50
                      : "transparent",
                  "&:hover:not(:disabled)": edge ? {} : { backgroundColor: color.gray100 },
                  "&:focus-visible": {
                    outline: `2px solid ${workspace.primary_dark}`,
                    outlineOffset: -2,
                  },
                }}
              >
                {day.getDate()}
              </Box>
            );
          })}
        </Box>

        <Typography sx={{ fontSize: 12, color: color.gray500, mt: 1 }}>
          {pending ? t("pickEnd", { start: shortDate(pending) }) : t("pickStart")}
        </Typography>
      </Popover>
    </>
  );
};

export default VoicesUsagePeriod;
